import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { UploadSimple, DownloadSimple, Trash, FileText, Paperclip, CheckCircle, Eye, EyeSlash } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";

const CATEGORY = "shipment-docs";

// Fixed slots — one downloadable file per type. "Shipping Bill" also auto-advances the
// job to "Docs Received" on the backend. Covers export + import paperwork.
// `portalHidden: true` marks slots the customer must never see — carrier↔forwarder
// paperwork and hazardous declarations. Must match PORTAL_HIDDEN_DOC_TYPES in
// backend/routes/portal.py; the backend is what actually enforces it.
const SLOTS = [
  { key: "Carrier Booking", hint: "Carrier booking confirmation", portalHidden: true },
  { key: "DG Declaration", hint: "Hazardous / DG declaration", portalHidden: true },
  { key: "Carrier MBL", hint: "Master B/L from the line" },
  { key: "House B/L", hint: "HBL issued to shipper" },
  { key: "Shipping Bill", hint: "Customs S/Bill (export)" },
  { key: "Bill of Entry", hint: "BOE (import)" },
  { key: "Commercial Invoice", hint: "Commercial invoice" },
  { key: "Packing List", hint: "Packing list" },
  { key: "Certificate of Origin", hint: "COO" },
  { key: "VGM", hint: "VGM / weight certificate" },
];
const SLOT_KEYS = SLOTS.map((s) => s.key);

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || "";
      const b64 = typeof result === "string" && result.includes(",") ? result.split(",", 2)[1] : result;
      resolve({ file_data: b64, file_name: file.name, file_type: file.type || "application/octet-stream" });
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

export default function ShipmentDocsPanel({ jobId, isCancelled }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = () => {
    setLoading(true);
    api.get(`/documents?job_id=${jobId}&category=${CATEGORY}`)
      .then(({ data }) => setDocs(data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [jobId]);

  const bySlot = (key) => docs.find((d) => d.doc_type === key);
  const others = docs.filter((d) => !SLOT_KEYS.includes(d.doc_type));

  const upload = async (file, docType, replaceId) => {
    if (!file) return;
    setBusy(docType);
    try {
      const { file_data, file_name, file_type } = await readFileAsBase64(file);
      if (replaceId) { try { await api.delete(`/documents/${replaceId}`); } catch { /* ignore */ } }
      await api.post(`/documents`, {
        job_id: jobId, doc_type: docType, name: file_name,
        file_name, file_data, file_type, category: CATEGORY,
      });
      toast.success(`Uploaded ${docType}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const download = async (doc) => {
    try {
      const { data } = await api.get(`/documents/${doc.id}/file`);
      const bin = atob(data.file_data || "");
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: data.file_type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.file_name || data.name || `${doc.doc_type}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete ${doc.doc_type} (${doc.file_name || doc.name})?`)) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Slots on the hidden list are never customer-visible whatever this flag says, so
  // the toggle is only offered on the ones where it's actually a judgement call.
  const slotAlwaysHidden = (docType) => !!SLOTS.find((s) => s.key === docType && s.portalHidden);
  const togglePortal = async (doc) => {
    const next = !doc.portal_hidden;
    try {
      await api.patch(`/documents/${doc.id}/portal-visibility`, { portal_hidden: next });
      toast.success(next ? "Hidden from the customer portal" : "Now visible in the customer portal");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const Row = ({ label, hint, doc }) => (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-[#272A35] last:border-b-0">
      <div className="w-52 shrink-0">
        <div className="text-sm text-gray-100 flex items-center gap-1.5">
          {doc ? <CheckCircle size={14} weight="fill" className="text-emerald-400" /> : <FileText size={14} className="text-gray-600" />}
          {label}
        </div>
        {hint && <div className="text-[10px] text-gray-500 uppercase tracking-wider mt-0.5">{hint}</div>}
      </div>
      <div className="flex-1 min-w-0">
        {doc ? (
          <button onClick={() => download(doc)} className="inline-flex items-center gap-1.5 text-sm text-[#F97316] hover:underline truncate max-w-full" title={doc.file_name || doc.name}>
            <DownloadSimple size={14} /> <span className="truncate">{doc.file_name || doc.name}</span>
          </button>
        ) : (
          <span className="text-xs text-gray-600">Not uploaded</span>
        )}
        {doc && doc.uploaded_at && <div className="text-[10px] text-gray-500 mt-0.5">{String(doc.uploaded_at).slice(0, 10)} · {doc.uploaded_by || ""}</div>}
        {doc && (slotAlwaysHidden(doc.doc_type) || doc.portal_hidden) && (
          <div className="text-[10px] text-gray-500 inline-flex items-center gap-1 mt-0.5">
            <EyeSlash size={10} /> Not shown to the customer
          </div>
        )}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {doc && !slotAlwaysHidden(doc.doc_type) && (
          <button onClick={() => togglePortal(doc)}
            title={doc.portal_hidden ? "Hidden from the customer portal — click to show" : "Visible in the customer portal — click to hide"}
            className={`h-8 w-8 rounded-md border border-[#272A35] inline-flex items-center justify-center ${
              doc.portal_hidden ? "text-gray-500 hover:text-sky-300" : "text-sky-300 hover:text-gray-400"}`}>
            {doc.portal_hidden ? <EyeSlash size={13} /> : <Eye size={13} />}
          </button>
        )}
        {!isCancelled && (
          <label className={`inline-flex items-center gap-1 h-8 px-3 rounded-md border border-[#272A35] hover:bg-[#15171D] text-xs cursor-pointer ${busy === label ? "opacity-50 pointer-events-none" : "text-gray-300"}`}>
            <UploadSimple size={13} /> {busy === label ? "Uploading…" : (doc ? "Replace" : "Upload")}
            <input type="file" className="hidden" onChange={(e) => { upload(e.target.files?.[0], label, doc?.id); e.target.value = ""; }} />
          </label>
        )}
        {doc && !isCancelled && (
          <button onClick={() => remove(doc)} className="h-8 w-8 rounded-md border border-[#272A35] text-gray-500 hover:text-red-400 inline-flex items-center justify-center" title="Delete"><Trash size={13} /></button>
        )}
      </div>
    </div>
  );

  return (
    <div className="bg-[#111318] border border-[#272A35] rounded-md">
      <div className="px-5 py-3 border-b border-[#272A35] flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-gray-500 flex items-center gap-2"><Paperclip size={13} /> Shipment Documents</div>
        <div className="text-[10px] text-gray-500">{docs.length} file{docs.length === 1 ? "" : "s"}</div>
      </div>

      {loading ? (
        <div className="py-8 text-center text-gray-500 text-xs font-mono uppercase tracking-widest">Loading…</div>
      ) : (
        <>
          <div>
            {SLOTS.map((s) => <Row key={s.key} label={s.key} hint={s.hint} doc={bySlot(s.key)} />)}
          </div>

          {/* Other / ad-hoc documents */}
          <div className="px-4 py-3 border-t border-[#272A35]">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs text-gray-400">Other documents</div>
              {!isCancelled && (
                <label className={`inline-flex items-center gap-1 h-8 px-3 rounded-md border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 text-xs cursor-pointer ${busy === "Other" ? "opacity-50 pointer-events-none" : ""}`}>
                  <UploadSimple size={13} /> {busy === "Other" ? "Uploading…" : "Add file"}
                  <input type="file" className="hidden" onChange={(e) => { upload(e.target.files?.[0], "Other", null); e.target.value = ""; }} />
                </label>
              )}
            </div>
            {others.length === 0 ? (
              <div className="text-xs text-gray-600">No other documents.</div>
            ) : (
              <div className="space-y-1">
                {others.map((d) => (
                  <div key={d.id} className="flex items-center gap-2 text-sm">
                    <button onClick={() => download(d)} className="inline-flex items-center gap-1.5 text-[#F97316] hover:underline truncate">
                      <DownloadSimple size={13} /> <span className="truncate">{d.file_name || d.name}</span>
                    </button>
                    {d.portal_hidden && <span className="text-[10px] text-gray-500 inline-flex items-center gap-1 shrink-0"><EyeSlash size={10} /> hidden</span>}
                    <button onClick={() => togglePortal(d)} className={`ml-auto ${d.portal_hidden ? "text-gray-500 hover:text-sky-300" : "text-sky-300 hover:text-gray-400"}`}
                      title={d.portal_hidden ? "Hidden from the customer portal — click to show" : "Visible in the customer portal — click to hide"}>
                      {d.portal_hidden ? <EyeSlash size={13} /> : <Eye size={13} />}
                    </button>
                    {!isCancelled && (
                      <button onClick={() => remove(d)} className="text-gray-500 hover:text-red-400" title="Delete"><Trash size={13} /></button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
